/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetWorkUnitsTrendRequest,
  GetWorkUnitsTrendSecurity,
} from "../models/operations/getworkunitstrend.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildWorkUnitsTrendQuery,
  prefetchWorkUnitsTrend,
  queryKeyWorkUnitsTrend,
  WorkUnitsTrendQueryData,
} from "./workUnitsTrend.core.js";
export {
  buildWorkUnitsTrendQuery,
  prefetchWorkUnitsTrend,
  queryKeyWorkUnitsTrend,
  type WorkUnitsTrendQueryData,
};

export type WorkUnitsTrendQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getWorkUnitsTrend chat
 *
 * @remarks
 * Aggregate work-units analysis results over time for the project: work done and cost/token efficiency per UTC day.
 */
export function useWorkUnitsTrend(
  request?: GetWorkUnitsTrendRequest | undefined,
  security?: GetWorkUnitsTrendSecurity | undefined,
  options?: QueryHookOptions<WorkUnitsTrendQueryData, WorkUnitsTrendQueryError>,
): UseQueryResult<WorkUnitsTrendQueryData, WorkUnitsTrendQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildWorkUnitsTrendQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getWorkUnitsTrend chat
 *
 * @remarks
 * Aggregate work-units analysis results over time for the project: work done and cost/token efficiency per UTC day.
 */
export function useWorkUnitsTrendSuspense(
  request?: GetWorkUnitsTrendRequest | undefined,
  security?: GetWorkUnitsTrendSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    WorkUnitsTrendQueryData,
    WorkUnitsTrendQueryError
  >,
): UseSuspenseQueryResult<WorkUnitsTrendQueryData, WorkUnitsTrendQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildWorkUnitsTrendQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setWorkUnitsTrendData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      from?: Date | undefined;
      to?: Date | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: WorkUnitsTrendQueryData,
): WorkUnitsTrendQueryData | undefined {
  const key = queryKeyWorkUnitsTrend(...queryKeyBase);

  return client.setQueryData<WorkUnitsTrendQueryData>(key, data);
}

export function invalidateWorkUnitsTrend(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      from?: Date | undefined;
      to?: Date | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "chat", "getWorkUnitsTrend", ...queryKeyBase],
  });
}

export function invalidateAllWorkUnitsTrend(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "chat", "getWorkUnitsTrend"],
  });
}
